import React, { useRef, useCallback } from 'react'
import Badge from '../components/Badge'
import { db } from '../..'
import { ref, set, get } from 'firebase/database'
import { getStorage, getDownloadURL, ref as storageRef } from 'firebase/storage'
import ImageModal from './ImageModal'
import TeamInfoModal from './TeamInfoModal'
import ReportModal from './ReportModal'
import ReportViewModal from './ReportViewModal'
import Paging from './components/Paging'
import ScoreGraphModal from './ScoreGraphModal'
import { SyncLoader } from 'react-spinners'

const History = (props) => {
    const storage = getStorage();
    const [gameLog, setGameLog] = React.useState({});
    const [historyData, setHistoryData] = React.useState([]); // 現在のページの履歴データのみ
    const [allHistoryKeys, setAllHistoryKeys] = React.useState([]); // 全履歴のキー一覧
    const [filteredHistoryKeys, setFilteredHistoryKeys] = React.useState([]); // フィルタリング後のキー一覧
    const [totalHistoryCount, setTotalHistoryCount] = React.useState(0); // 履歴の総数
    const [myLikes, setMyLikes] = React.useState([]);
    const [reportCounts, setReportCounts] = React.useState({}); // 各履歴の通報件数
    const teamSelect = useRef(null);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [totalPages, setTotalPages] = React.useState(1);
    const [imageModalOpen, setImageModalOpen] = React.useState(false);
    const [imageURL, setImageURL] = React.useState('');
    const [teamModalOpen, setTeamModalOpen] = React.useState(false);
    const [modalTeam, setModalTeam] = React.useState(null);
    const [members, setMembers] = React.useState([]);
    const [likeDisabled, setLikeDisabled] = React.useState(false);
    const [scoreGraphOpen, setScoreGraphOpen] = React.useState(false);
    const [scoreGraphHistory, setScoreGraphHistory] = React.useState([]);
    const [scoreGraphLoading, setScoreGraphLoading] = React.useState(false);
    const [loading, setLoading] = React.useState(false); // ページ読み込み中フラグ

    // 通報関連の状態
    const [reportModalOpen, setReportModalOpen] = React.useState(false);
    const [reportViewModalOpen, setReportViewModalOpen] = React.useState(false);
    const [selectedHistoryForReport, setSelectedHistoryForReport] = React.useState(null);
    const [openMenuIndex, setOpenMenuIndex] = React.useState(null); // 開いているメニューのインデックス

    const ITEMS_PER_PAGE = 10; // 1ページあたりの表示件数

    // ゲームデータの取得
    React.useEffect(() => {
        if(!props.gameId) return;

        // ゲームメンバーの取得
        const membersRef = ref(db, 'games/' + props.gameId + '/members');
        get(membersRef).then(snapshot => {
            if(snapshot.exists()) {
                setMembers(snapshot.val());
            }
        });

        // ゲーム基本情報の取得（チーム情報など）
        const gameRef = ref(db, 'games/' + props.gameId);
        get(gameRef).then(snapshot => {
            if(snapshot.exists()) {
                setGameLog({
                    teams: snapshot.val().teams,
                    missions: snapshot.val().missions
                });
            }
        });

        // 履歴の総数とキー一覧を取得
        const gameLogRef = ref(db, 'gameLogs/' + props.gameId + '/history');
        get(gameLogRef).then(snapshot => {
            if(snapshot.exists()) {
                const allKeys = Object.keys(snapshot.val()).reverse(); // 新しい順にソート
                setAllHistoryKeys(allKeys);
                setFilteredHistoryKeys(allKeys); // 初期状態では全履歴を表示
                setTotalHistoryCount(allKeys.length);
                setTotalPages(Math.ceil(allKeys.length / ITEMS_PER_PAGE));
            }
        });

    }, [props.gameId]);

    // 画像URLを非同期で取得する関数
    const loadPhotosAsync = useCallback(async (pageData, startIndex) => {
        for (let i = 0; i < pageData.length; i++) {
            const historyItem = pageData[i];

            if (historyItem.report && historyItem.report.photo && historyItem.report.photo.length > 0) {
                try {
                    const photoPromises = historyItem.report.photo.map(async (photo) => {
                        try {
                            const photoPath = "gameLogs/" + props.gameId + "/missions/" + historyItem.missionId + "/reports/" + String(historyItem.teamId) + "/" + photo;
                            const photoRef = storageRef(storage, photoPath);
                            return await getDownloadURL(photoRef);
                        } catch (error) {
                            console.error('Photo load error:', error);
                            return null;
                        }
                    });

                    const photoURLs = await Promise.all(photoPromises);
                    const validPhotoURLs = photoURLs.filter(url => url !== null);

                    // 取得できた画像URLで状態を更新
                    setHistoryData(prevData => {
                        const newData = [...prevData];
                        const targetIndex = newData.findIndex(item => item.key === historyItem.key);
                        if (targetIndex !== -1) {
                            newData[targetIndex] = {
                                ...newData[targetIndex],
                                photoURL: validPhotoURLs,
                                loadingPhotos: false
                            };
                        }
                        return newData;
                    });
                } catch (error) {
                    console.error('Error loading photos for item:', error);
                    // エラーの場合もローディングフラグを解除
                    setHistoryData(prevData => {
                        const newData = [...prevData];
                        const targetIndex = newData.findIndex(item => item.key === historyItem.key);
                        if (targetIndex !== -1) {
                            newData[targetIndex] = {
                                ...newData[targetIndex],
                                loadingPhotos: false
                            };
                        }
                        return newData;
                    });
                }
            }
        }
    }, [props.gameId, storage]);

    // 指定されたページのデータを読み込む
    const loadPageData = useCallback(async (pageNumber) => {
        setLoading(true);
        
        const startIndex = (pageNumber - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        const pageKeys = filteredHistoryKeys.slice(startIndex, endIndex);
        
        const pageData = [];
        
        // まず履歴データのみを取得して表示
        for (let i = 0; i < pageKeys.length; i++) {
            const key = pageKeys[i];
            try {
                const historyRef = ref(db, 'gameLogs/' + props.gameId + '/history/' + key);
                const snapshot = await get(historyRef);
                
                if (snapshot.exists()) {
                    let history = snapshot.val();
                    
                    pageData.push({
                        ...history,
                        index: startIndex + i,
                        key: key,
                        photoURL: [], // 初期状態では空配列
                        loadingPhotos: history.report && history.report.photo && history.report.photo.length > 0 // 画像があるかフラグ
                    });
                }
            } catch (error) {
                console.error('Error loading history item:', error);
            }
        }
        
        // 履歴データを先に表示
        setHistoryData(pageData);
        setLoading(false);
        
        // 画像URLを非同期で取得
        loadPhotosAsync(pageData, startIndex);
    }, [props.gameId, filteredHistoryKeys, loadPhotosAsync]);

    // 現在のページの履歴データを取得
    React.useEffect(() => {
        if(!props.gameId) return;

        // 絞り込み結果が0件の場合は、前回表示分が残らないようクリアする
        if(filteredHistoryKeys.length === 0) {
            setHistoryData([]);
            return;
        }

        loadPageData(currentPage);
    }, [props.gameId, filteredHistoryKeys, currentPage, loadPageData]);

    // いいねしたログの取得
    React.useEffect(() => {
        if((props.likes && props.gameId) && props.likes[props.gameId]) {
            setMyLikes(Object.keys(props.likes[props.gameId]));
        }
    }, [props.likes, props.gameId]);

    // いいねボタン
    const likeBtn = async (index, logKey) => {
        setLikeDisabled(true);
        let beforeLikes = historyData[index].likes ? historyData[index].likes : 0;

        try {
            // 現在のいいね数を取得
            await get(ref(db, 'gameLogs/' + props.gameId + '/history/' + logKey + '/likes'))
            .then((snapshot) => {
                if(snapshot.exists()) {
                    beforeLikes = snapshot.val();
                }
            });

            await set(ref(db, 'gameLogs/' + props.gameId + '/history/' + logKey + '/likes'), beforeLikes + 1);
            await set(ref(db, 'users/' + props.uid + '/likes/' + props.gameId + '/' + logKey), true);

            // いいねリストに追加
            setMyLikes([...myLikes, logKey]);
            // いいね数を更新
            setHistoryData(prev => {
                const newData = [...prev];
                newData[index] = {
                    ...newData[index],
                    likes: beforeLikes + 1
                };
                return newData;
            });
        } catch (error) {
            console.error('いいねの登録に失敗しました:', error);
            alert('いいねの登録に失敗しました。もう一度お試しください。');
        } finally {
            // 成功・失敗にかかわらず、必ずボタンを再度有効化する
            setLikeDisabled(false);
        }
    }

    // いいね解除ボタン
    const unLikeBtn = async (index, logKey) => {
        setLikeDisabled(true);
        let beforeLikes = historyData[index].likes ? historyData[index].likes : 0;

        try {
            // 現在のいいね数を取得
            await get(ref(db, 'gameLogs/' + props.gameId + '/history/' + logKey + '/likes'))
            .then((snapshot) => {
                if(snapshot.exists()) {
                    beforeLikes = snapshot.val();
                }
            });

            await set(ref(db, 'gameLogs/' + props.gameId + '/history/' + logKey + '/likes'), beforeLikes - 1);
            await set(ref(db, 'users/' + props.uid + '/likes/' + props.gameId + '/' + logKey), null);

            // いいねリストから削除
            setMyLikes(myLikes.filter((key) => key !== logKey));
            // いいね数を更新
            setHistoryData(prev => {
                const newData = [...prev];
                newData[index] = {
                    ...newData[index],
                    likes: beforeLikes - 1
                };
                return newData;
            });
        } catch (error) {
            console.error('いいねの解除に失敗しました:', error);
            alert('いいねの解除に失敗しました。もう一度お試しください。');
        } finally {
            // 成功・失敗にかかわらず、必ずボタンを再度有効化する
            setLikeDisabled(false);
        }
    }

    // フィルタリング後の履歴キーを更新する
    const updateFilteredKeys = async (filterTeamId) => {
        if (!allHistoryKeys.length) return;
        
        if (filterTeamId === null) {
            // フィルタなしの場合は全履歴を表示
            setFilteredHistoryKeys(allHistoryKeys);
            setTotalHistoryCount(allHistoryKeys.length);
            setTotalPages(Math.ceil(allHistoryKeys.length / ITEMS_PER_PAGE));
            setCurrentPage(1);
            return;
        }

        // フィルタリング対象の履歴を抽出
        const filteredKeys = [];
        
        for (const key of allHistoryKeys) {
            try {
                const historyRef = ref(db, 'gameLogs/' + props.gameId + '/history/' + key);
                const snapshot = await get(historyRef);
                
                if (snapshot.exists()) {
                    const history = snapshot.val();
                    
                    if (filterTeamId === -1) {
                        // コントローラ（運営）のみ表示
                        if (history.teamId === undefined) {
                            filteredKeys.push(key);
                        }
                    } else {
                        // 指定チームのみ表示
                        if (history.teamId === filterTeamId) {
                            filteredKeys.push(key);
                        }
                    }
                }
            } catch (error) {
                console.error('Error filtering history item:', error);
            }
        }
        
        setFilteredHistoryKeys(filteredKeys);
        setTotalHistoryCount(filteredKeys.length);
        setTotalPages(Math.ceil(filteredKeys.length / ITEMS_PER_PAGE));
        setCurrentPage(1); // フィルタリング時は1ページ目に戻る
    };

    // 絞り込み
    const filterBtn = () => {
        if(teamSelect.current.value === 'すべて') {
            updateFilteredKeys(null);
        } else {
            const selectedTeamId = teamSelect.current.selectedIndex - 2;
            updateFilteredKeys(selectedTeamId);
        }
    }

    // スコアグラフを開く（ページングとは独立して、全履歴を時系列順に取得する）
    const openScoreGraph = async () => {
        setScoreGraphLoading(true);

        try {
            const historyRef = ref(db, 'gameLogs/' + props.gameId + '/history');
            const snapshot = await get(historyRef);

            if(snapshot.exists()) {
                const historyList = Object.values(snapshot.val()).sort((a, b) => new Date(a.time) - new Date(b.time));
                setScoreGraphHistory(historyList);
            } else {
                setScoreGraphHistory([]);
            }

            setScoreGraphOpen(true);
        } catch (error) {
            console.error('スコアグラフ用履歴の取得に失敗しました:', error);
            alert('スコアグラフの読込みに失敗しました。もう一度お試しください。');
        } finally {
            setScoreGraphLoading(false);
        }
    };

    // ページ変更
    const changePage = (num) => {
        if(currentPage + num > 0 && currentPage + num <= totalPages) {
            setCurrentPage(currentPage + num);
        }
    }

    // Google Mapのリンク（ピン付き）を開く
    const openMap = (position) => {
        const mapUrl = `https://maps.google.com/maps?ll=${position.lat},${position.lng}&z=15&q=${position.lat},${position.lng}`;
        window.open(mapUrl, '_blank');
    };

    // 通報モーダルを開く
    const openReportModal = (historyItem) => {
        setSelectedHistoryForReport(historyItem);
        setReportModalOpen(true);
        setOpenMenuIndex(null);
    };

    // 通報確認モーダルを開く
    const openReportViewModal = (historyItem) => {
        setSelectedHistoryForReport(historyItem);
        setReportViewModalOpen(true);
        setOpenMenuIndex(null);
    };

    // メニューの切り替え
    const toggleMenu = (index) => {
        setOpenMenuIndex(openMenuIndex === index ? null : index);
    };

    // メニュー外クリックで閉じる
    React.useEffect(() => {
        const handleClickOutside = () => {
            setOpenMenuIndex(null);
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    // 通報件数の取得
    React.useEffect(() => {
        if (!props.gameId || filteredHistoryKeys.length === 0) return;

        const loadReportCounts = async () => {
            const counts = {};
            for (const key of filteredHistoryKeys) {
                try {
                    const reportsRef = ref(db, `gameLogs/${props.gameId}/reports/${key}`);
                    const snapshot = await get(reportsRef);
                    if (snapshot.exists()) {
                        counts[key] = Object.keys(snapshot.val()).length;
                    }
                } catch (error) {
                    console.error('通報件数取得エラー:', error);
                }
            }
            setReportCounts(counts);
        };

        loadReportCounts();
    }, [props.gameId, filteredHistoryKeys]);

    return (
        <div className='lg:grid lg:grid-cols-3'>
            <div className='p-3'>
                <div className='rounded-lg bg-white shadow-sm p-4 mb-6'>
                    <h3 className='text-center mb-3 text-gray-700'>絞り込み</h3>
                    <h4 className='text-lg text-purple-800 mb-2'><i className='bi bi-filter me-2'></i>チーム</h4>
                    <div className='mb-3'>
                        <select className='border border-gray-200 p-2 rounded-md w-full focus:outline-purple-300' ref={teamSelect}>
                            <option>すべて</option>
                            <option>コントローラ</option>
                            {
                                gameLog.teams && Object.keys(gameLog.teams).map((teamKey, index) => (
                                    <option key={teamKey}>{gameLog.teams[teamKey].name}</option>
                                ))
                            }
                        </select>
                    </div>

                    <div>
                        <button className='bg-purple-500 text-white p-2 px-4 rounded-lg hover:bg-purple-600 w-full' onClick={() => filterBtn()}><i className='bi bi-search me-2'></i>検索</button>
                    </div>
                </div>

                {/* スコアグラフ */}
                <button className='rounded-lg bg-white shadow-sm p-4 mb-6 text-center hover:bg-gray-50 w-full' onClick={() => openScoreGraph()} disabled={scoreGraphLoading}>
                    <i className='bi bi-graph-up text-purple-400 mb-3 text-4xl'></i>
                    <p className='text-gray-600 text-sm mt-2'>{scoreGraphLoading ? '読込み中...' : 'スコアグラフ'}</p>
                </button>
            </div>

            <div className='col-span-2'>

                {/* ページング */}
                {
                    totalHistoryCount > 0 && (
                        <div className='mx-3'>
                            <Paging changePage={(e) => changePage(e)} currentPage={currentPage} totalPages={totalPages} />
                        </div>
                    )
                }
                {/* ローディング表示 */}
                {loading && (
                    <div className='text-center py-8'>
                        <SyncLoader color="#bf8bceff" />
                        <p className='mt-2 text-gray-600'>読み込み中...</p>
                    </div>
                )}

                {/* 履歴表示 */}
                {!loading && historyData.map((historyItem, index) => {
                    const hasReports = reportCounts[historyItem.key] > 0;
                    const isSubmissionHistory = historyItem.actionNum === 4; // ミッション提出履歴

                    return (
                        <div className='relative rounded-lg bg-white shadow-sm m-3 px-3 py-2' key={historyItem.key}>
                            {/* チームデータがあればチームを表示、なければコントローラを表示 */}
                                {historyItem.team ? (
                                    <div className='flex items-center justify-between text-gray-700 ' onClick={() => {setModalTeam(gameLog.teams?.[historyItem.teamId] ?? historyItem.team);setTeamModalOpen(true);}}>
                                        <div className='text-xl hover:bg-gray-50 p-1 px-2 cursor-pointer rounded-lg'>
                                            {/* チーム名など */}
                                            <i className='bi bi-people-fill me-2' style={{color: historyItem.team.color}}></i>
                                            {historyItem.team.name} 
                                        </div>

                                        {/* 通報件数表示とメニューボタン（ミッション提出履歴のみ） */}
                                        {isSubmissionHistory && (
                                            <div className='flex items-center gap-2'>
                                                {/* 通報件数表示 */}
                                                {hasReports && (
                                                    <button
                                                        onClick={() => openReportViewModal(historyItem)}
                                                        className='text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full hover:bg-red-200'
                                                    >
                                                        <i className='bi bi-flag-fill me-1'></i>
                                                        通報{reportCounts[historyItem.key]}件
                                                    </button>
                                                )}
                                                
                                                {/* 三点リーダメニュー */}
                                                <div className='relative'>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleMenu(index);
                                                        }}
                                                        className='text-gray-500 hover:text-gray-700 p-1'
                                                    >
                                                        <i className='bi bi-three-dots-vertical text-sm'></i>
                                                    </button>
                                                    
                                                    {/* ドロップダウンメニュー */}
                                                    {openMenuIndex === index && (
                                                        <div 
                                                            className='absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-32'
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <button
                                                                onClick={() => openReportModal(historyItem)}
                                                                className='w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-t-lg'
                                                            >
                                                                <i className='bi bi-flag me-2'></i>
                                                                通報する
                                                            </button>
                                                            {hasReports && (
                                                                <button
                                                                    onClick={() => openReportViewModal(historyItem)}
                                                                    className='w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 border-t border-gray-100 rounded-b-lg'
                                                                >
                                                                    <i className='bi bi-eye me-2'></i>
                                                                    通報を確認
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className='text-gray-700 text-base p-1'><i className='bi bi-flag text-gray-500 me-2' style={{}}></i>コントローラ（運営）</div>
                                )}

                                {/* 履歴のタイトル */}
                                <div className='mx-2 text-gray-600 mb-2 text-sm flex justify-between items-top'>
                                    <span>{historyItem.action}</span>
                                </div>

                                {/* 提出したミッションのタイトルとコメント */}
                                {historyItem.actionNum === 4 && (
                                    <div className='mb-2'>
                                        <Badge icon="flag" text={historyItem.missionTitle} />をクリア！<br />
                                        <div className={historyItem.report.comment ? 'p-2 whitespace-pre-wrap' : 'hidden'}><i className='bi bi-pencil me-2 text-gray-400'></i>{historyItem.report.comment}</div>
                                        {
                                            // 提出画像があれば表示
                                            (historyItem.photoURL && historyItem.photoURL.length > 0) ? (
                                                <div className='mt-3 m-2 flex gap-2 items-center'>
                                                    {historyItem.photoURL.map((url, index2) => (
                                                        <img 
                                                            key={"img_" + index + "_" + index2} 
                                                            src={url} 
                                                            alt="提出画像" 
                                                            className='w-1/2 max-w-40 h-32 object-cover rounded-lg border inline-block cursor-pointer' 
                                                            onClick={() => {setImageURL(url);setImageModalOpen(true);}} 
                                                        />
                                                    ))}
                                                </div>
                                            ) : historyItem.loadingPhotos ? (
                                                <div className='mt-2 p-4 text-center text-gray-500'>
                                                    <SyncLoader size={8} color="#6b7280" />
                                                    <div className='mt-2 text-sm'>画像を読み込み中...</div>
                                                </div>
                                            ) : null
                                        }
                                    </div>
                                )}

                                {/* 提出削除したミッション */}
                                {historyItem.actionNum === 10 && (
                                    <div className='mb-2'>
                                        <Badge icon="flag" text={historyItem.missionTitle} color="red" />の提出を取り消しました。
                                    </div>
                                )}

                                {/* 発令したミッションの情報 */}
                                {historyItem.actionNum === 3 && (
                                    <div className='mb-2'>
                                        <Badge icon="flag" text={historyItem.mission.name} color="sky" />を発令！
                                    </div>
                                )}

                                {/* ステータスの更新 */}
                                {historyItem.actionNum === 2 && (
                                    <div className='mb-2'>
                                        <Badge icon="clock" text={historyItem.status.typeText} color="orange" />になりました。
                                    </div>
                                )}

                                {/* アクティベート */}
                                {historyItem.actionNum === 0 && (
                                    <div className='mb-2 ms-2'>
                                        ゲームの実行が有効化されました。
                                    </div>
                                )}

                                {/* リタイア */}
                                {historyItem.actionNum === 6 && (
                                    <div className='mb-2'>
                                        <Badge icon="x-lg" text="リタイア" color="red" />しました。
                                    </div>
                                )}

                                {/* リタイア取消し */}
                                {historyItem.actionNum === 8 && (
                                    <div className='mb-2 ms-2'>
                                        リタイアを取り消し、<Badge icon="arrow-clockwise" text="ゲームに復帰" color="green" />しました。
                                    </div>
                                )}

                                {/* 役交代 */}
                                {historyItem.actionNum === 7 && (
                                    <div className='mb-2'>
                                        <Badge icon="people-fill" text={historyItem.teams?.find(team => Number(team.role) === 1)?.name} color="red" />が
                                        <Badge icon="people-fill" text={historyItem.teams?.find(team => Number(team.role) === 0)?.name} color="sky" />をつかまえました。
                                    </div>
                                )}

                                {/* ミッション内容の変更 */}
                                {historyItem.actionNum === 9 && (
                                    <div className='mb-2'>
                                        <Badge icon="flag" text={historyItem.mission.name} color="sky" />の内容が変更されました。
                                    </div>
                                )}

                                {/* 特別カードの使用 */}
                                {historyItem.actionNum === 5 && (
                                    <div className='mb-2'>
                                        <Badge icon="collection" text={historyItem.card.name} color="purple" />を使用しました。
                                    </div>
                                )}

                                {/* ミッションの締切 */}
                                {historyItem.actionNum === 12 && (
                                    <div className='mb-2'>
                                        <Badge icon="flag" text={historyItem.mission.name} color="sky" />の提出を締め切りました。

                                        {historyItem.mission.reports && (
                                            <div className='py-2 px-1 mt-2'>
                                                <h5 className='text-green-800'>提出済み</h5>
                                                <div className=''>
                                                    {
                                                        historyItem.mission?.reports && Object.keys(historyItem.mission.reports).map((teamId) => (
                                                            <div className='inline-block py-2'>
                                                                <Badge key={index + "_" + teamId} icon="people-fill" text={gameLog.teams?.[Number(teamId)]?.name} />
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                            </div>
                                        )}

                                        {(historyItem.noSubmitTeams && historyItem.noSubmitTeams.length > 0) && (
                                            <div className='py-2 px-1'>
                                                <h5 className='text-red-800'>未提出</h5>
                                                <div className=''>
                                                    {
                                                        historyItem.noSubmitTeams.map((team, teamIndex) => (
                                                            <div className='inline-block py-2'>
                                                                <Badge color="red" key={index + "_" + (teamIndex + 1000)} icon="people-fill" text={team.name} />
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* ランキングの確定 */}
                                {historyItem.actionNum === 11 && (
                                    <div className='mb-2'>
                                        お疲れさまでした。ゲームが終了し、ランキングが確定しました。

                                        <div className='mt-2'>
                                            <table className='w-full text-center'>
                                                <thead className='bg-lime-50 text-lime-800'>
                                                    <tr>
                                                        <th>順位</th>
                                                        <th>チーム名</th>
                                                        <th>スコア</th>
                                                    </tr>
                                                </thead>
                                                <tbody className='text-gray-700'>
                                                    {
                                                        historyItem.ranking && historyItem.ranking.map((team, rankIndex) => (
                                                            <tr key={"rank_" + rankIndex} className='border-b border-gray-100'>
                                                                <td>{team.rank}</td>
                                                                <td>{team.name}</td>
                                                                <td>{team.score}</td>
                                                            </tr>
                                                        ))
                                                    }
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}


                                <div className='flex justify-between mt-3'>
                                    <div className='ps-2 flex gap-2'>
                                        {/* リアクションボタン */}
                                        {
                                            myLikes.indexOf(historyItem.key) !== -1 ? (
                                                <button className='cursor-pointer bg-pink-50 text-pink-600 px-2 py-1 text-sm rounded-lg hover:bg-pink-100 border' disabled={likeDisabled} onClick={() => unLikeBtn(index, historyItem.key)}><i className='bi bi-heart-fill me-1'></i>{historyItem.likes ? historyItem.likes : 0}</button>
                                            ) : (
                                                <button className='cursor-pointer bg-white-50 text-gray-600 px-2 py-1 text-sm rounded-lg hover:bg-gray-100 border' disabled={likeDisabled} onClick={() => likeBtn(index, historyItem.key)}><i className='bi bi-heart me-1'></i>{historyItem.likes ? historyItem.likes : 0}</button>
                                            )
                                        }

                                        {/* マップボタン（ミッション） */}
                                        { historyItem?.report?.position && (
                                            <button className='cursor-pointer bg-blue-50 text-blue-600 px-2 py-1 text-sm rounded-lg hover:bg-blue-100 border' onClick={() => openMap(historyItem.report.position)}>
                                                <i className='bi bi-map me-1'></i>位置情報
                                            </button>
                                        )}

                                        {/* マップボタン（役交代） */}
                                        { historyItem?.position && (
                                            <button className='cursor-pointer bg-blue-50 text-blue-600 px-2 py-1 text-sm rounded-lg hover:bg-blue-100 border' onClick={() => openMap(historyItem.position)}>
                                                <i className='bi bi-map me-1'></i>位置情報
                                            </button>
                                        )}
                                    </div>

                                    {/* 日時 */}
                                    <div className='text-gray-500 text-sm'><i className='bi bi-clock me-1'></i>{new Date(historyItem.time).toLocaleString()}</div>
                                </div>
                            </div>
                        )
                    })
                }

                {
                    allHistoryKeys.length === 0 ? (
                        <div className='text-gray-600 text-lg text-center my-6'>アクティベートされていません</div>
                    ) : totalHistoryCount === 0 ? (
                        <div className='text-gray-600 text-lg text-center my-6'>該当する履歴がありません</div>
                    ) : (
                        <div className='mx-3'>
                            <Paging changePage={(e) => changePage(e)} currentPage={currentPage} totalPages={totalPages} />
                        </div>
                    )
                }

                
            </div>

            <ImageModal isOpen={imageModalOpen} onClose={() => setImageModalOpen(false)} url={imageURL} />

            <TeamInfoModal isOpen={teamModalOpen} onClose={() => setTeamModalOpen(false)} team={modalTeam} members={members} />

            <ScoreGraphModal isOpen={scoreGraphOpen} onClose={() => setScoreGraphOpen(false)} history={scoreGraphHistory} teams={gameLog.teams ? Object.values(gameLog.teams) : []} missions={gameLog.missions || {}} />

            {/* 通報モーダル */}
            <ReportModal
                isOpen={reportModalOpen}
                onClose={() => {
                    setReportModalOpen(false);
                    setSelectedHistoryForReport(null);
                }}
                gameId={props.gameId}
                historyKey={selectedHistoryForReport?.key}
                historyItem={selectedHistoryForReport}
                uid={props.uid}
            />

            {/* 通報確認モーダル */}
            <ReportViewModal
                isOpen={reportViewModalOpen}
                onClose={() => {
                    setReportViewModalOpen(false);
                    setSelectedHistoryForReport(null);
                }}
                gameId={props.gameId}
                historyKey={selectedHistoryForReport?.key}
                historyItem={selectedHistoryForReport}
            />
        </div>
    )
}

export default History